import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Plus, Save, Trash2, Users, X } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { UserPhoto } from '@/components/UserPhoto'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { AvatarStack, initialsOf } from '../../components/AvatarStack'
import { platformApi } from '../../api'
import type { Group } from '../../types'
import { useAdmin } from './AdminData'
import { groupIdsInDefinition } from './model'
import { PersonPicker, type PickedPerson } from './PersonPicker'

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong')

function CreateGroupDialog({ open, onOpenChange, onCreated }: {
    open: boolean
    onOpenChange: (o: boolean) => void
    onCreated: (g: Group) => void
}) {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => { if (open) { setName(''); setDescription(''); setError('') } }, [open])

    const create = async () => {
        if (!name.trim()) { setError('Give the group a name'); return }
        setSaving(true)
        setError('')
        try {
            const g = await platformApi.createGroup(name.trim(), description.trim())
            toast.success('Group created')
            onOpenChange(false)
            onCreated(g)
        } catch (e) {
            setError(errMsg(e))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <form onSubmit={(e) => { e.preventDefault(); void create() }} className="flex flex-col gap-4">
                    <DialogHeader>
                        <DialogTitle>New group</DialogTitle>
                        <DialogDescription>Groups let a workflow step be assigned to a whole team.</DialogDescription>
                    </DialogHeader>
                    <FieldGroup>
                        <Field data-invalid={!!error || undefined}>
                            <FieldLabel htmlFor="grp-name">Name</FieldLabel>
                            <Input id="grp-name" autoFocus value={name} aria-invalid={!!error || undefined} onChange={(e) => setName(e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="grp-desc">Description</FieldLabel>
                            <Textarea id="grp-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </Field>
                    </FieldGroup>
                    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={saving}>
                            {saving && <Spinner data-icon="inline-start" />} Create group
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

function GroupSheet({ groupId, onClose, onChanged }: { groupId: string | null; onClose: () => void; onChanged: () => void }) {
    const admin = useAdmin()
    const [group, setGroup] = useState<Group | null>(null)
    const [members, setMembers] = useState<PickedPerson[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (!groupId) return
        let cancelled = false
        setLoading(true); setError(''); setGroup(null)
        platformApi.getGroup(groupId)
            .then((g) => { if (!cancelled) { setGroup(g); setMembers(g.members ?? []) } })
            .catch((e) => { if (!cancelled) setError(errMsg(e)) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [groupId])

    const usedBy = useMemo(
        () => admin.workflows.filter((w) => groupId && groupIdsInDefinition(w.definition).includes(groupId)),
        [admin.workflows, groupId],
    )

    const saveMembers = async () => {
        if (!group) return
        setSaving(true); setError('')
        try {
            const g = await platformApi.setGroupMembers(group.id, members.map((m) => m.id))
            toast.success('Members saved')
            setGroup(g)
            admin.ensureMembers([group.id], true)
            onChanged()
        } catch (e) {
            setError(errMsg(e))
        } finally {
            setSaving(false)
        }
    }

    const remove = async () => {
        if (!group) return
        setDeleting(true)
        try {
            await platformApi.deleteGroup(group.id)
            toast.success('Group deleted')
            setConfirmDelete(false)
            onClose()
            onChanged()
        } catch (e) {
            setConfirmDelete(false)
            setError(errMsg(e))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <>
            <Sheet open={!!groupId} onOpenChange={(o) => { if (!o) onClose() }}>
                <SheetContent className="w-full data-[side=right]:sm:max-w-lg">
                    <SheetHeader>
                        <SheetTitle>{group?.name ?? 'Group'}</SheetTitle>
                        <SheetDescription>{group?.description || 'Manage who belongs to this group.'}</SheetDescription>
                    </SheetHeader>
                    <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
                        {error && (
                            <Alert variant="destructive"><AlertTriangle /><AlertTitle>{error}</AlertTitle></Alert>
                        )}
                        {loading && <Skeleton className="h-32" />}
                        {group && (
                            <>
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold">Members ({members.length})</h3>
                                    <PersonPicker mode="add" placeholder="Add person" exclude={members.map((m) => m.id)}
                                        onSelect={(p) => setMembers((m) => [...m, p])} />
                                </div>
                                {members.length === 0 ? (
                                    <Empty className="border">
                                        <EmptyHeader><EmptyTitle>No members</EmptyTitle><EmptyDescription>Add people to this group.</EmptyDescription></EmptyHeader>
                                    </Empty>
                                ) : (
                                    <ul className="flex flex-col gap-2">
                                        {members.map((m) => (
                                            <li key={m.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
                                                <Avatar>
                                                    <UserPhoto id={m.id} />
                                                    <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initialsOf(m.name)}</AvatarFallback>
                                                </Avatar>
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm font-medium">{m.name}</div>
                                                    <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                                                </div>
                                                <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove ${m.name}`}
                                                    onClick={() => setMembers((l) => l.filter((x) => x.id !== m.id))}><X /></Button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                    <SheetFooter className="border-t sm:flex-row sm:justify-between">
                        <Button type="button" variant="destructive" disabled={!group} onClick={() => setConfirmDelete(true)}>
                            <Trash2 data-icon="inline-start" /> Delete group
                        </Button>
                        <Button type="button" disabled={!group || saving} onClick={saveMembers}>
                            {saving ? <Spinner data-icon="inline-start" /> : <Save data-icon="inline-start" />} Save members
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete {group?.name}?</DialogTitle>
                        <DialogDescription>This removes the group and its memberships. It cannot be undone.</DialogDescription>
                    </DialogHeader>
                    {usedBy.length > 0 && (
                        <Alert variant="destructive">
                            <AlertTriangle />
                            <AlertTitle>Used by {usedBy.length} {usedBy.length === 1 ? 'workflow' : 'workflows'}</AlertTitle>
                            <AlertDescription>
                                {usedBy.map((w) => w.name).join(', ')}. Steps assigned to this group will have no one to review them.
                            </AlertDescription>
                        </Alert>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                        <Button type="button" variant="destructive" disabled={deleting} onClick={remove}>
                            {deleting && <Spinner data-icon="inline-start" />} Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default function GroupsTab() {
    const admin = useAdmin()
    const [creating, setCreating] = useState(false)
    const [selected, setSelected] = useState<string | null>(null)
    const { ensureMembers, groups } = admin

    useEffect(() => { ensureMembers(groups.map((g) => g.id)) }, [groups, ensureMembers])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">Groups are reusable sets of reviewers for workflow steps.</p>
                <Button type="button" onClick={() => setCreating(true)}><Plus data-icon="inline-start" /> New group</Button>
            </div>

            {admin.loading ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-36" />)}</div>
            ) : groups.length === 0 ? (
                <Empty className="border">
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><Users /></EmptyMedia>
                        <EmptyTitle>No groups yet</EmptyTitle>
                        <EmptyDescription>Create a group such as "Ethics board" and assign it to a workflow step.</EmptyDescription>
                    </EmptyHeader>
                </Empty>
            ) : (
                <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {groups.map((g) => (
                        <li key={g.id}>
                            <Card className="h-full">
                                <CardHeader>
                                    <CardTitle>{g.name}</CardTitle>
                                    <CardDescription>{g.description || 'No description'} · {g.member_count} {g.member_count === 1 ? 'member' : 'members'}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <AvatarStack people={(admin.members[g.id] ?? []).map((m) => ({ ...m, via: `group:${g.name}` }))} />
                                </CardContent>
                                <CardFooter>
                                    <Button type="button" variant="outline" size="sm" onClick={() => setSelected(g.id)}>Manage members</Button>
                                </CardFooter>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}

            <CreateGroupDialog open={creating} onOpenChange={setCreating}
                onCreated={(g) => { void admin.reloadGroups(); setSelected(g.id) }} />
            <GroupSheet groupId={selected} onClose={() => setSelected(null)}
                onChanged={() => void admin.reloadGroups()} />
        </div>
    )
}
